/**
 * Creating a category, as a guided sequence rather than a form that leaves you
 * on a page you can't use yet.
 *
 * A category can't generate until it has a briefing and instructions for each
 * AI stage (the API refuses otherwise), so those aren't "settings to find
 * later" — they're steps. The wizard ends with one create call that produces a
 * category ready to work.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button, Input } from '@servicecompressionllc/sc-design-system';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { knowledgeApi, type KnowledgeStage } from './api.js';
import { Field, StageEditor, TextArea } from './stageFields.js';
import { DraftWithAi } from './DraftWithAi.js';
import { useMe } from '../../hooks/useMe.js';
import { canWriteKnowledge } from '../../lib/knowledgeAccess.js';

type Step = 'basics' | 'briefing' | 'list' | 'item' | 'review';
const STEPS: Array<{ id: Step; label: string }> = [
  { id: 'basics', label: 'What it is' },
  { id: 'briefing', label: 'What the AI should know' },
  { id: 'list', label: 'Finding what to write' },
  { id: 'item', label: 'Writing each guide' },
  { id: 'review', label: 'Review' },
];

/**
 * Placeholder only — it shows the SHAPE of a briefing without pretending to be
 * one. Deliberately generic and bracketed: this ships to every tenant, so a
 * concrete example from one customer's domain would be both wrong for everyone
 * else and a piece of that customer's vocabulary baked into the product.
 */
const BRIEFING_PLACEHOLDER = `These are [what they are], used for [what they do].

They are NOT [the thing people confuse them with] — don't reference [components that belong to that other thing].

Our team calls [something] a "[the word they actually use]".

[Anything a newcomer would get wrong: a distinction between models, a constraint that only applies sometimes.]`;

/** Matches the API's create defaults; the wizard sends all of it explicitly. */
const DEFAULT_STAGE: KnowledgeStage = {
  mode: 'LLM_DEBATE',
  prompt: '',
  strictness: 'STRICT',
};

export const CategoryWizard = () => {
  const navigate = useNavigate();
  const me = useMe();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('basics');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [subjectNoun, setSubjectNoun] = useState('');
  const [itemNoun, setItemNoun] = useState('');
  const [promptContext, setPromptContext] = useState('');
  const [describe, setDescribe] = useState('');
  const suggest = useMutation({
    mutationFn: () => knowledgeApi.suggestCategory(describe.trim()),
    onSuccess: (out) => {
      setName(out.name);
      setSubjectNoun(out.subjectNoun);
      setItemNoun(out.itemNoun);
      setPromptContext(out.promptContext);
    },
  });
  const [list, setList] = useState<KnowledgeStage>(DEFAULT_STAGE);
  const [item, setItem] = useState<KnowledgeStage>(DEFAULT_STAGE);

  const nouns = {
    subject: subjectNoun.trim().length > 0 ? subjectNoun.trim() : 'subject',
    item: itemNoun.trim().length > 0 ? itemNoun.trim() : 'guide',
  };
  /** What the API stores when a noun is left blank. */
  const nounValues = {
    subject: subjectNoun.trim().length > 0 ? subjectNoun.trim() : 'Subject',
    item: itemNoun.trim().length > 0 ? itemNoun.trim() : 'Item',
  };

  // Composed by the API using the SAME function the worker uses, so the preview
  // can't drift from the real prompt.
  const preview = useMutation({
    mutationFn: (stage: 'list' | 'item') =>
      knowledgeApi.previewPrompt({
        stage,
        promptContext,
        prompt: stage === 'list' ? list.prompt : item.prompt,
        strictness: stage === 'list' ? list.strictness : item.strictness,
        subjectNoun: nounValues.subject,
        itemNoun: nounValues.item,
      }),
  });

  const create = useMutation({
    mutationFn: () =>
      knowledgeApi.createTemplate({
        name: name.trim(),
        description: description.trim().length > 0 ? description.trim() : null,
        subjectNoun: subjectNoun.trim().length > 0 ? subjectNoun.trim() : 'Subject',
        itemNoun: itemNoun.trim().length > 0 ? itemNoun.trim() : 'Item',
        promptContext,
        list,
        item,
      }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['knowledge', 'templates'] });
      void navigate(`/knowledge/categories/${created.id}`);
    },
  });

  const stepIndex = STEPS.findIndex((s) => s.id === step);
  const canLeaveBasics = name.trim().length > 0;
  const canLeaveBriefing = promptContext.trim().length > 0;
  const stageReady = (stage: KnowledgeStage): boolean =>
    stage.mode === 'MANUAL' || stage.prompt.trim().length > 0;

  // Read-only users reach this by URL — every other entry point hides the
  // button. Send them back rather than letting them fill in five steps and
  // collect a 403 at the end.
  if (me.data && !canWriteKnowledge(me.data.user)) {
    return <Navigate to="/knowledge/categories" replace />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <section>
        <Breadcrumbs trail={[{ label: 'Categories', to: '/knowledge/categories' }]} />
        <h2 className="text-2xl font-bold text-ink">New category</h2>
      </section>

      {/* Progress as words, not a number: each step says what it is for. */}
      <ol className="flex flex-wrap gap-1 border-b border-outline" role="list">
        {STEPS.map((s, i) => (
          <li key={s.id}>
            <span
              aria-current={s.id === step ? 'step' : undefined}
              className={`-mb-px block border-b-2 px-4 py-2 text-sm font-medium ${
                s.id === step
                  ? 'border-primary text-ink'
                  : i < stepIndex
                    ? 'border-transparent text-ink-muted'
                    : 'border-transparent text-ink-subtle'
              }`}
            >
              {i + 1}. {s.label}
            </span>
          </li>
        ))}
      </ol>

      <div className="max-w-3xl">
        {step === 'basics' && (
          <div className="rounded-lg border border-outline bg-surface-elevated p-4">
            <div className="mb-4 rounded-lg border border-outline p-3">
              <label className="block text-sm font-medium text-ink">
                Start from a description
                <span className="mt-0.5 block text-xs font-normal text-ink-muted">
                  Optional. Say what this covers in your own words and the fields below — including
                  the briefing — get filled in for you to check and edit.
                </span>
                <Input
                  value={describe}
                  aria-label="Describe what this category covers"
                  placeholder="What these things are, what your team does with them, and the words you use for them."
                  onChange={(e) => setDescribe(e.target.value)}
                  className="mt-1 w-full"
                />
              </label>
              <div className="mt-2 flex items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={() => suggest.mutate()}
                  disabled={suggest.isPending || describe.trim().length < 10}
                >
                  {suggest.isPending ? 'Working…' : 'Suggest the setup'}
                </Button>
                {suggest.error && (
                  <span className="text-sm text-danger">{(suggest.error as Error).message}</span>
                )}
              </div>
            </div>
            <Field label="Category name" hint="What this group of things is called.">
              <Input
                value={name}
                aria-label="Category name"
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full"
              />
            </Field>
            <Field label="Description" hint="Optional. A line for your team.">
              <Input
                value={description}
                aria-label="Category description"
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 w-full"
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="What you call one of these" hint="“Component”, “Unit”, “Panel”.">
                <Input
                  value={subjectNoun}
                  aria-label="Subject noun"
                  placeholder="Component"
                  onChange={(e) => setSubjectNoun(e.target.value)}
                  className="mt-1 w-full"
                />
              </Field>
              <Field label="What you call one of its guides" hint="“Failure mode”, “Procedure”.">
                <Input
                  value={itemNoun}
                  aria-label="Item noun"
                  placeholder="Failure mode"
                  onChange={(e) => setItemNoun(e.target.value)}
                  className="mt-1 w-full"
                />
              </Field>
            </div>
          </div>
        )}

        {step === 'briefing' && (
          <div className="rounded-lg border border-outline bg-surface-elevated p-4">
            <Field
              label="Category briefing"
              hint={`What the AI must know before writing anything about a ${nouns.subject}: what this equipment is and isn't, the words your field uses, anything it should never assume. This goes into every prompt for every ${nouns.subject} here.`}
            >
              <TextArea
                value={promptContext}
                onChange={setPromptContext}
                label="Category briefing"
                rows={12}
                placeholder={BRIEFING_PLACEHOLDER}
              />
            </Field>
            <DraftWithAi
              kind="briefing"
              label="Have the AI draft this"
              gistPlaceholder="What this equipment is and what your team does with it."
              categoryName={name}
              subjectNoun={nouns.subject}
              itemNoun={nouns.item}
              onAccept={setPromptContext}
            />
          </div>
        )}

        {step === 'list' && (
          <>
            <DraftWithAi
              kind="list"
              label="Have the AI draft these instructions"
              gistPlaceholder={`What should a complete set of ${nouns.item}s cover?`}
              categoryName={name}
              briefing={promptContext}
              subjectNoun={nouns.subject}
              itemNoun={nouns.item}
              onAccept={(text) => setList({ ...list, prompt: text })}
            />
            <StageEditor
              title={`Deciding which ${nouns.item}s to cover`}
              blurb={`Produces a list of ${nouns.item}s for your team to review. Nothing gets written until you keep the entries you want.`}
              stage={list}
              stageName="Step 1"
              stageKind="list"
              onChange={setList}
            />
          </>
        )}

        {step === 'item' && (
          <>
            <DraftWithAi
              kind="item"
              label="Have the AI draft these instructions"
              gistPlaceholder={`What does a good ${nouns.item} look like?`}
              categoryName={name}
              briefing={promptContext}
              subjectNoun={nouns.subject}
              itemNoun={nouns.item}
              onAccept={(text) => setItem({ ...item, prompt: text })}
            />
            <StageEditor
              title={`Writing each ${nouns.item}`}
              blurb="One guide per entry you kept, each arriving as a draft for review."
              stage={item}
              stageName="Step 2"
              stageKind="item"
              onChange={setItem}
            />
          </>
        )}

        {step === 'review' && (
          <div className="rounded-lg border border-outline bg-surface-elevated p-4">
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase tracking-wide text-ink-subtle">Category</dt>
                <dd className="text-sm text-ink">{name}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-ink-subtle">Calls things</dt>
                <dd className="text-sm text-ink">
                  {nouns.subject} / {nouns.item}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-ink-subtle">Step 1</dt>
                <dd className="text-sm text-ink">
                  {list.mode === 'MANUAL'
                    ? 'by hand'
                    : `${list.mode === 'LLM_DEBATE' ? 'review pass' : 'single pass'}, ${
                        list.strictness === 'STRICT' ? 'only my documents' : 'may fill gaps'
                      }`}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-ink-subtle">Step 2</dt>
                <dd className="text-sm text-ink">
                  {item.mode === 'MANUAL'
                    ? 'by hand'
                    : `${item.mode === 'LLM_DEBATE' ? 'review pass' : 'single pass'}, ${
                        item.strictness === 'STRICT' ? 'only my documents' : 'may fill gaps'
                      }`}
                </dd>
              </div>
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">
              {(['list', 'item'] as const).map((stage) => (
                <Button
                  key={stage}
                  variant="ghost"
                  onClick={() => preview.mutate(stage)}
                  disabled={preview.isPending}
                >
                  {preview.isPending
                    ? 'Loading…'
                    : `See what step ${stage === 'list' ? '1' : '2'} sends the AI`}
                </Button>
              ))}
            </div>
            {preview.data && (
              <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap rounded border border-outline bg-surface p-3 text-xs text-ink">
                {preview.data.text}
              </pre>
            )}
            {preview.error && (
              <p className="mt-2 text-sm text-danger">{(preview.error as Error).message}</p>
            )}
            {create.error && (
              <p className="mt-3 text-sm text-danger">{(create.error as Error).message}</p>
            )}
          </div>
        )}
      </div>

      <div className="flex max-w-3xl items-center gap-2">
        {stepIndex > 0 && (
          <Button variant="ghost" onClick={() => setStep(STEPS[stepIndex - 1]?.id ?? 'basics')}>
            Back
          </Button>
        )}
        {step !== 'review' ? (
          <Button
            onClick={() => setStep(STEPS[stepIndex + 1]?.id ?? 'review')}
            disabled={
              (step === 'basics' && !canLeaveBasics) ||
              (step === 'briefing' && !canLeaveBriefing) ||
              (step === 'list' && !stageReady(list)) ||
              (step === 'item' && !stageReady(item))
            }
          >
            Next
          </Button>
        ) : (
          <Button onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create category'}
          </Button>
        )}
        <Button variant="ghost" onClick={() => void navigate('/knowledge/categories')}>
          Cancel
        </Button>
        {((step === 'list' && !stageReady(list)) || (step === 'item' && !stageReady(item))) && (
          <span className="text-sm text-ink-muted">
            This step needs instructions, or set it to by hand.
          </span>
        )}
      </div>
    </div>
  );
};
